import React, { forwardRef, useEffect, useState } from 'react'
import { Team } from '../../types/Team'
import { useNavigate } from 'react-router'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import {
  CreateEngagementDto,
  Engagement,
  UpdateEngagementDto,
} from '../../types/Engagement'
import { Client } from '../../types/Client'
import { CalendarBlank, CaretDown, X } from '@phosphor-icons/react'
import moment from 'moment'
import { toast } from '../../utils/toast'
interface Props {
  handleCreateSubmit?: (args: CreateEngagementDto) => Promise<void>
  handleEditSubmit?: (args: UpdateEngagementDto) => Promise<void>
  displayErrorBanner?: boolean
  isDuplicateNameError?: boolean
  loading: boolean
  engagement?: Engagement
  companyId: number
  clients: Client[]
  teams: Team[]
}

interface DateFieldInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  success?: boolean
}

const DateFieldInput = forwardRef<HTMLInputElement, DateFieldInputProps>(
  ({ success, ...props }, ref) => (
    <div className="engagement-form__date-wrapper">
      <input
        {...props}
        ref={ref}
        className={`engagement-form__input engagement-form__date-input ${
          success ? 'engagement-form__input--success' : ''
        }`}
      />
      <span className="engagement-form__date-icon">
        <CalendarBlank size={14} weight="regular" />
      </span>
    </div>
  ),
)
DateFieldInput.displayName = 'DateFieldInput'

export const EngagementForm = ({
  handleCreateSubmit,
  handleEditSubmit,
  displayErrorBanner,
  isDuplicateNameError,
  loading,
  engagement,
  companyId,
  teams,
  clients,
}: Props) => {
  const [projectName, setProjectName] = useState('')
  const [description, setDescription] = useState('')
  const [techStack, setTechStack] = useState<string[]>([])
  const [industries, setIndustries] = useState<string[]>([])
  const [weeklyRate] = useState<number>(0)
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [selectedClient, setSelectedClient] = useState<number>(0)
  const [selectedTeam, setSelectedTeam] = useState<number>(-1)
  const [currentTech, setCurrentTech] = useState('')
  const [currentIndustry, setCurrentIndustry] = useState('')
  const [projectNameTouched, setProjectNameTouched] = useState(false)
  const [startDateTouched, setStartDateTouched] = useState(false)
  const [endDateTouched, setEndDateTouched] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    if (engagement) {
      setProjectName(engagement.projectName || '')
      setDescription(engagement.description || '')
      setTechStack(engagement.techStack || [])
      setIndustries(engagement.industries || [])
      setStartDate(engagement.startDate ? new Date(engagement.startDate) : null)
      setEndDate(engagement.endDate ? new Date(engagement.endDate) : null)
      setSelectedClient(engagement.client?.id || 0)
      setSelectedTeam(engagement.team?.id || 0)
    }
  }, [engagement])

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!selectedTeam || !selectedClient || !startDate) return
    const args = {
      projectName: projectName.trim(),
      techStack,
      industries,
      weeklyRate: engagement?.weeklyRate ?? weeklyRate,
      teamId: selectedTeam,
      clientId: selectedClient,
      startDate,
      endDate,
      companyId,
      description,
    }
    engagement
      ? handleEditSubmit &&
        handleEditSubmit({
          ...args,
          id: engagement.id,
        })
      : handleCreateSubmit &&
        handleCreateSubmit({
          ...args,
        })
  }

  const handleOnBack = () => {
    navigate(-1)
  }

  const withError = (isProjectNameField = false) => {
    if (!displayErrorBanner) return ''
    if (isDuplicateNameError && !isProjectNameField) return ''
    return 'engagement-form__input--error'
  }
  const withSuccess = (value: unknown) =>
    value && !displayErrorBanner ? 'engagement-form__input--success' : ''

  return (
    <>
      <form className="col-12 engagement-form" onSubmit={handleFormSubmit}>
        <section className="engagement-form__section">
          <div className="engagement-form__section-heading">
            <div className="engagement-form__section-header">
              <h3 className="engagement-form__section-title">
                General Information
              </h3>
            </div>
            <p className="engagement-form__required-note">(*) Required</p>
          </div>
          <div className="engagement-form__grid">
            <div className="engagement-form__field">
              <label
                htmlFor="engagementFormProjectNameInput"
                className="engagement-form__label"
              >
                Project Name{' '}
                <span className="engagement-form__required">*</span>
              </label>
              <input
                type="text"
                className={`engagement-form__input ${withError(
                  true,
                )} ${projectNameTouched ? withSuccess(projectName) : ''}`}
                id="engagementFormProjectNameInput"
                aria-describedby="projectNameHelp"
                value={projectName}
                onChange={e => {
                  setProjectName(e.target.value)
                  setProjectNameTouched(true)
                }}
                maxLength={50}
                aria-label="form-project-name-input"
              />
            </div>
            <div className="engagement-form__field">
              <label
                htmlFor="engagementFormClientInput"
                className="engagement-form__label"
              >
                Client <span className="engagement-form__required">*</span>
              </label>
              <div className="engagement-form__select-wrapper">
                <select
                  className={`engagement-form__select-input ${withSuccess(
                    selectedClient,
                  )}`}
                  value={selectedClient}
                  id="engagementFormClientInput"
                  onChange={e => {
                    setSelectedClient(parseInt(e.target.value))
                  }}
                >
                  <option hidden value={0}>
                    Select a Client
                  </option>
                  {clients.map((client, index) => (
                    <option key={`client-${index}`} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
                <CaretDown
                  size={14}
                  weight="regular"
                  className="engagement-form__select-icon"
                />
              </div>
            </div>
            <div className="engagement-form__field">
              <label
                htmlFor="engagementFormTeamInput"
                className="engagement-form__label"
              >
                Team <span className="engagement-form__required">*</span>
              </label>
              <div className="engagement-form__select-wrapper">
                <select
                  className={`engagement-form__select-input ${withSuccess(
                    selectedTeam,
                  )}`}
                  value={selectedTeam}
                  id="engagementFormTeamInput"
                  onChange={e => {
                    if (startDate || endDate) {
                      setEndDate(null)
                      setStartDate(null)
                    }
                    setSelectedTeam(parseInt(e.target.value))
                  }}
                >
                  <option hidden value={0}>
                    Select a Team
                  </option>
                  {teams
                    .filter(team => {
                      if (
                        !team.endDate ||
                        moment().isSameOrBefore(moment(team.startDate))
                      )
                        return true

                      return moment().isBetween(
                        team.startDate,
                        team.endDate,
                        undefined,
                        '[]',
                      )
                    })
                    .map((team, index) => (
                      <option key={`team-${index}`} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                </select>
                <CaretDown
                  size={14}
                  weight="regular"
                  className="engagement-form__select-icon"
                />
              </div>
            </div>
          </div>
          <div className="engagement-form__grid">
            <div className="engagement-form__field">
              <label className="engagement-form__label">
                Started On <span className="engagement-form__required">*</span>
              </label>
              <DatePicker
                customInput={
                  <DateFieldInput
                    success={
                      !!startDate && startDateTouched && !displayErrorBanner
                    }
                  />
                }
                maxDate={endDate}
                selected={startDate}
                placeholderText={'MM/DD/YYYY'}
                onChange={date => {
                  if (date) {
                    const newDate = moment(date)
                    if (selectedTeam === -1) {
                      toast.warning(`You must select a team before`)
                      return
                    }
                    const formsSelectedTeam = teams.find(
                      value => value.id === selectedTeam,
                    )
                    if (!formsSelectedTeam) return

                    const selectedTeamStartDate = moment(
                      formsSelectedTeam.startDate,
                    )
                    const selectedTeamEndDate = moment(
                      formsSelectedTeam.endDate,
                    )

                    if (
                      (formsSelectedTeam.endDate &&
                        !newDate.isBetween(
                          selectedTeamStartDate,
                          selectedTeamEndDate,
                          undefined,
                          '[]',
                        )) ||
                      newDate.isBefore(selectedTeamStartDate)
                    ) {
                      toast.warning(
                        `Looks like team ${formsSelectedTeam.name} does not exist on the start date you selected`,
                      )
                      return
                    }

                    if (endDate && date > endDate) {
                      setEndDate(date)
                      setEndDateTouched(true)
                    }
                    setStartDate(date)
                    setStartDateTouched(true)
                  }
                }}
              />
            </div>
            <div className="engagement-form__field">
              <label className="engagement-form__label">
                Ended On <span className="engagement-form__required">*</span>
              </label>
              <DatePicker
                customInput={
                  <DateFieldInput
                    success={
                      !!endDate && endDateTouched && !displayErrorBanner
                    }
                  />
                }
                selected={endDate}
                placeholderText={'MM/DD/YYYY'}
                minDate={startDate}
                onChange={date => {
                  if (date) {
                    if (selectedTeam === -1) {
                      toast.warning(`You must select a team before`)
                      return
                    }
                    const newDate = moment(date)
                    const formsSelectedTeam = teams.find(
                      value => value.id === selectedTeam,
                    )
                    if (!formsSelectedTeam) return

                    const selectedTeamStartDate = moment(
                      formsSelectedTeam.startDate,
                    )
                    const selectedTeamEndDate = moment(
                      formsSelectedTeam.endDate,
                    )
                    if (
                      formsSelectedTeam.endDate &&
                      !newDate.isBetween(
                        selectedTeamStartDate,
                        selectedTeamEndDate,
                        undefined,
                        '[]',
                      )
                    ) {
                      toast.warning(
                        `Looks like team ${formsSelectedTeam.name} does not exist on the end date you selected`,
                      )
                      return
                    }
                    if (!startDate) {
                      setEndDate(date)
                    } else if (date < startDate) {
                      setEndDate(startDate)
                    } else {
                      setEndDate(date)
                    }
                    setEndDateTouched(true)
                  }
                }}
              />
            </div>
          </div>
          <div className="engagement-form__field">
            <label
              htmlFor="engagementDescriptionInput"
              className="engagement-form__label"
            >
              Description
            </label>
            <textarea
              placeholder="Add a Description (500 characters max)"
              className={`engagement-form__input engagement-form__textarea ${withError()}`}
              id="engagementTechStackInput"
              aria-describedby="descriptionHelp"
              value={description}
              onChange={e => {
                setDescription(e.target.value)
              }}
              rows={6}
              maxLength={500}
              aria-label="form-tech-stack-input"
            />
          </div>
        </section>

        <section className="engagement-form__section">
          <div className="engagement-form__section-heading">
            <div className="engagement-form__section-header">
              <h3 className="engagement-form__section-title">
                Tech Stack & Toolset
              </h3>
            </div>
          </div>
          <div className="engagement-form__tag-group">
            <input
              type="text"
              placeholder="e.g. Design Thinking, AWS, Scrum Master..."
              className={`engagement-form__input ${withError()}`}
              id="engagementTechStackInput"
              aria-describedby="techStackHelp"
              value={currentTech}
              onChange={e => {
                setCurrentTech(e.target.value)
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  if (currentTech) {
                    setTechStack([
                      ...currentTech.split(',').filter(t => t),
                      ...techStack,
                    ])
                    setCurrentTech('')
                  }
                }
              }}
              maxLength={50}
              aria-label="form-tech-stack-input"
            />
            {techStack.length > 0 && (
              <div className="engagement-form__tags">
                {(techStack || []).map((tech, index) => (
                  <button
                    key={`tech-${index}`}
                    type="button"
                    className="engagement-form__tag"
                    onClick={e => {
                      e.preventDefault()
                      setTechStack([
                        ...techStack.filter(newTech => newTech !== tech),
                      ])
                    }}
                  >
                    <span>{tech}</span>
                    <X size={12} weight="bold" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="engagement-form__section">
          <div className="engagement-form__section-heading">
            <div className="engagement-form__section-header">
              <h3 className="engagement-form__section-title">Industries</h3>
            </div>
          </div>
          <div className="engagement-form__tag-group">
            <input
              type="text"
              placeholder="e.g. Finance, Tech, Project Management..."
              className={`engagement-form__input ${withError()}`}
              id="engagementIndustryInput"
              aria-describedby="industriesHelp"
              value={currentIndustry}
              onChange={e => {
                setCurrentIndustry(e.target.value)
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  if (currentIndustry) {
                    setIndustries([
                      ...currentIndustry.split(',').filter(i => i),
                      ...industries,
                    ])
                    setCurrentIndustry('')
                  }
                }
              }}
              maxLength={50}
              aria-label="form-industries-input"
            />
            {industries.length > 0 && (
              <div className="engagement-form__tags">
                {industries.map((industry, index) => (
                  <button
                    key={`industry-${index}`}
                    type="button"
                    className="engagement-form__tag"
                    onClick={e => {
                      e.preventDefault()
                      setIndustries([
                        ...industries.filter(
                          newIndustry => newIndustry !== industry,
                        ),
                      ])
                    }}
                  >
                    <span>{industry}</span>
                    <X size={12} weight="bold" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        <div className="engagement-form__actions">
          <button
            type="button"
            className="engagement-form__btn engagement-form__btn--cancel"
            onClick={handleOnBack}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="engagement-form__btn engagement-form__btn--submit"
            disabled={
              !projectName.trim() ||
              !startDate ||
              !endDate ||
              !selectedClient ||
              !selectedTeam ||
              loading
            }
            aria-label="login-submit-button"
          >
            {engagement ? 'Update' : 'Save'}
          </button>
        </div>
      </form>
    </>
  )
}
